import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, TableWrap } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { botLine, type ChatAuthor } from '../lib/chatbot.ts';
import { clock, dateTime, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi } from './Reports.tsx';

/**
 * Live chat: the website's chat seen from inside. Colleagues say they are available, take the
 * chats that come in, answer with ready-made replies, hand a chat to someone else, close it, and
 * turn a promising one into a sales opportunity. The report shows how fast and how well.
 */

type View = 'active' | 'mine' | 'waiting' | 'closed' | 'all';
type Tab = 'inbox' | 'canned' | 'bot' | 'report';
type Author = ChatAuthor;

interface Inbox {
  settings: { enabled: boolean; title: string };
  me: { id: string; available: boolean };
  online: { id: string; name: string }[];
  team: { id: string; name: string; online: boolean }[];
  counts: { active: number; waiting: number; mine: number; unread: number };
  chats: Row[];
  can: { manage: boolean };
}
interface Row {
  id: string;
  ref: string;
  name: string;
  email: string | null;
  state: 'open' | 'closed';
  offline: boolean;
  operator: string | null;
  operator_name: string | null;
  last_at: string;
  last_from: Author;
  unread: number;
  preview: string;
  rating: number | null;
  bot: boolean;
  bot_solved: boolean;
}
interface Msg {
  id: string;
  author: Author;
  body: string;
  at: string;
  user_name: string | null;
}
interface Detail {
  id: string;
  ref: string;
  name: string;
  email: string | null;
  page: string | null;
  state: 'open' | 'closed';
  offline: boolean;
  operator: string | null;
  operator_name: string | null;
  first_reply_seconds: number | null;
  rating: number | null;
  rating_note: string | null;
  started_at: string;
  closed_at: string | null;
  seen_by_visitor: boolean;
  bot: boolean;
  bot_solved: boolean;
  bot_name: string;
  messages: Msg[];
  earlier: { id: string; ref: string; at: string; state: string }[];
  lead: { id: string; title: string } | null;
  can: { lead: boolean; manage: boolean };
}
interface Canned {
  id: string;
  shortcut: string;
  body: string;
  uses: number;
}
interface Settings {
  enabled: boolean;
  title: string;
  welcome: string | null;
  offline_text: string | null;
  ask_email: 'no' | 'optional' | 'required';
  rating: boolean;
}
interface Report {
  days: number;
  chats: number;
  offline: number;
  unanswered: number;
  median_reply: number | null;
  ratings: { good: number; okay: number; bad: number };
  leads: number;
  bot_solved: number;
  by_hour: number[];
  operators: { id: string; name: string; chats: number; sent: number; median_reply: number | null; good_pct: number | null }[];
  comments: { id: string; ref: string; name: string; rating: number; note: string; at: string | null }[];
}

const VIEWS: View[] = ['active', 'mine', 'waiting', 'closed', 'all'];
const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'inbox', icon: 'inbox' },
  { key: 'canned', icon: 'note' },
  { key: 'bot', icon: 'sparkle' },
  { key: 'report', icon: 'chart' },
];
const FACE: Record<number, string> = { 3: '🙂', 2: '😐', 1: '🙁' };
const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
const seconds = (s: number | null) => (s === null ? '—' : s < 60 ? t('lc.secs', { n: s }) : t('lc.mins', { n: Math.round(s / 60) }));
const chatUrl = (tenant: string) => `${window.location.origin}/chat/${tenant}`;

function systemText(body: string) {
  const at = body.indexOf(':');
  const kind = at < 0 ? body : body.slice(0, at);
  const rest = at < 0 ? '' : body.slice(at + 1);
  if (kind === 'transfer') return t('lc.sys_transfer', { name: rest });
  if (body === 'closed:bot') return t('lc.sys_closed_bot');
  if (kind === 'closed') return rest === 'visitor' ? t('lc.sys_closed_visitor') : t('lc.sys_closed_staff');
  return body;
}

export function LiveChat() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'inbox') as Tab;
  const { tenant } = useSession();
  const [box, setBox] = useState<Inbox | null>(null);
  const [view, setView] = useState<View>('active');
  const [q, setQ] = useState('');
  const [settings, setSettings] = useState(false);
  const { fail } = useUi();
  const selected = params.get('c');
  const key = `${view}|${q.trim()}`;
  const load = useCallback(() => {
    const [v, text] = key.split('|');
    return action<Inbox>('livechat.inbox', { view: v, q: text || null }).then(setBox, fail);
  }, [key, fail]);
  useEffect(() => {
    const id = window.setTimeout(() => void load(), q ? 250 : 0);
    return () => window.clearTimeout(id);
  }, [load, q]);
  // Live: every new message or change of hands reloads the list. The slow check also keeps an
  // available colleague counted as online while the page is open.
  // One stream for the page: the open chat hears about its own changes through `pulse`.
  const [pulse, setPulse] = useState(0);
  const open = useRef(selected);
  open.current = selected;
  useEffect(
    () =>
      subscribe(['livechat'], (_e, data: { conversation?: string }) => {
        void load();
        if (!data?.conversation || data.conversation === open.current) setPulse((n) => n + 1);
      }),
    [load],
  );
  useEffect(() => {
    const id = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(id);
  }, [load]);

  const select = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('c', id);
    else p.delete('c');
    setParams(p, { replace: !!selected === !!id });
  };
  const setTab = (k: Tab) => setParams(k === 'inbox' ? {} : { tab: k }, { replace: true });
  const available = box?.me.available ?? false;
  const toggle = async (next: boolean) => {
    if (!box) return;
    setBox({ ...box, me: { ...box.me, available: next } });
    await action('livechat.set_available', { available: next }).then(() => load(), fail);
  };
  const others = box?.online.filter((o) => o.id !== box.me.id) ?? [];

  return (
    <Shell>
      <div className="page reports lc-page">
        <div className="page-head">
          <div>
            <h1>{t('nav.livechat')}</h1>
            <p className="muted">{t('lc.subtitle')}</p>
          </div>
          <div className="page-actions lc-head-actions">
            {box ? (
              <label className={`lc-presence${available ? ' on' : ''}`}>
                <span className="lc-presence-dot" aria-hidden="true" />
                <span className="lc-presence-text">
                  <strong>{available ? t('lc.available') : t('lc.away')}</strong>
                  <small>{others.length ? t('lc.also_online', { names: others.map((o) => o.name.split(' ')[0]).join(', ') }) : available ? t('lc.only_you') : t('lc.nobody')}</small>
                </span>
                <Switch checked={available} onChange={(v) => void toggle(v)} label={t('lc.available_switch')} />
              </label>
            ) : null}
            {box?.can.manage ? <IconButton icon="settings" label={t('lc.settings')} onClick={() => setSettings(true)} /> : null}
          </div>
        </div>
        {box && !box.settings.enabled ? (
          <div className="callout callout-warn lc-banner" role="status">
            <Icon name="alert" size={18} />
            <span>{box.can.manage ? t('lc.off_manage') : t('lc.off')}</span>
            {box.can.manage ? (
              <Button size="sm" variant="primary" onClick={() => setSettings(true)}>
                {t('lc.switch_on')}
              </Button>
            ) : null}
          </div>
        ) : box && !box.online.length ? (
          <div className="callout callout-info lc-banner" role="status">
            <Icon name="moon" size={18} />
            <span>{t('lc.nobody_banner')}</span>
          </div>
        ) : null}
        <div className="segmented tabs" role="tablist" aria-label={t('nav.livechat')}>
          {TABS.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setTab(x.key)}>
              <Icon name={x.icon} size={16} />
              {t(`lc.tab_${x.key}` as MessageKey)}
              {x.key === 'inbox' && box && box.counts.waiting + box.counts.unread ? (
                <span className="count lc-count" aria-label={tn('lc.need_you', box.counts.waiting + box.counts.unread)}>
                  {box.counts.waiting + box.counts.unread}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        {tab === 'inbox' ? (
          !box ? (
            <Skeleton h={480} r="var(--radius-lg)" />
          ) : (
            <div className={`lc-inbox${selected ? ' has-chat' : ''}`}>
              <aside className="lc-list panel" aria-label={t('lc.chats')}>
                <div className="lc-list-tools">
                  <div className="lc-search">
                    <Icon name="search" size={16} />
                    <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('lc.search')} aria-label={t('lc.search')} />
                  </div>
                  <div className="lc-views" role="group" aria-label={t('lc.show')}>
                    {VIEWS.map((v) => (
                      <button key={v} type="button" aria-pressed={view === v} className={`chip${view === v ? ' active' : ''}`} onClick={() => setView(v)}>
                        {t(`lc.v_${v}` as MessageKey)}
                        {v !== 'closed' && v !== 'all' && box.counts[v] ? <span className="count lc-chip-n">{box.counts[v]}</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
                {!box.chats.length ? (
                  <EmptyState icon="chat" title={q ? t('lc.none_found') : t(`lc.empty_${view}` as MessageKey)} body={q ? undefined : view === 'active' ? t('lc.empty_body') : undefined} />
                ) : (
                  <ul className="lc-rows">
                    {box.chats.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className={`lc-row${selected === c.id ? ' active' : ''}${c.unread ? ' unread' : ''}`}
                          aria-current={selected === c.id || undefined}
                          onClick={() => select(c.id)}
                        >
                          <span className={`lc-avatar${c.state === 'closed' ? ' closed' : ''}`} aria-hidden="true">
                            {initials(c.name)}
                          </span>
                          <span className="lc-row-main">
                            <span className="lc-row-top">
                              <strong>{c.name}</strong>
                              <time dateTime={c.last_at}>{since(c.last_at)}</time>
                            </span>
                            <span className="lc-row-preview">
                              {c.last_from === 'staff' ? <span className="muted">{t('lc.you_prefix')} </span> : null}
                              {botLine(c.last_from, c.preview)?.text ?? (c.last_from === 'system' ? systemText(c.preview) : c.preview)}
                            </span>
                            <span className="lc-row-tags">
                              {c.state === 'closed' ? (
                                c.bot_solved ? (
                                  <span className="tag lc-tag-bot">
                                    <Icon name="sparkle" size={12} /> {t('lc.bot_solved')}
                                  </span>
                                ) : (
                                  <span className="tag">{t('lc.closed')}</span>
                                )
                              ) : c.bot ? (
                                <span className="tag lc-tag-bot">
                                  <Icon name="sparkle" size={12} /> {t('lc.with_bot')}
                                </span>
                              ) : !c.operator ? (
                                <span className="tag lc-tag-warn">
                                  <Icon name="clock" size={12} /> {c.offline ? t('lc.left_message') : t('lc.waiting')}
                                </span>
                              ) : c.operator === box.me.id ? (
                                <span className="tag lc-tag-ok">{t('lc.yours')}</span>
                              ) : (
                                <span className="tag">{c.operator_name}</span>
                              )}
                              {c.rating ? <span aria-label={t(`lc.rating_${c.rating}` as MessageKey)}>{FACE[c.rating]}</span> : null}
                            </span>
                          </span>
                          {c.unread ? (
                            <span className="count lc-count" aria-label={tn('lc.unread', c.unread)}>
                              {c.unread}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </aside>
              <section className="lc-thread-pane panel" aria-label={t('lc.conversation')}>
                {selected ? (
                  <Thread key={selected} id={selected} pulse={pulse} me={box.me.id} team={box.team} onBack={() => select(null)} onChanged={() => void load()} onOpen={select} />
                ) : (
                  <EmptyState icon="chat" title={t('lc.pick')} body={box.counts.waiting ? tn('lc.pick_waiting', box.counts.waiting) : t('lc.pick_body')} />
                )}
              </section>
            </div>
          )
        ) : tab === 'canned' ? (
          <CannedTab />
        ) : tab === 'bot' ? (
          <BotTab />
        ) : (
          <ReportTab />
        )}
        {settings && tenant ? <SettingsDialog tenant={tenant.slug} onClose={() => setSettings(false)} onSaved={() => void load()} /> : null}
      </div>
    </Shell>
  );
}

function Thread({
  id,
  pulse,
  me,
  team,
  onBack,
  onChanged,
  onOpen,
}: {
  id: string;
  pulse: number;
  me: string;
  team: Inbox['team'];
  onBack: () => void;
  onChanged: () => void;
  onOpen: (id: string) => void;
}) {
  const [d, setD] = useState<Detail | null>(null);
  const { fail, toast } = useUi();
  const { can } = useSession();
  const { run, busy } = useSave();
  const [body, setBody] = useState('');
  const [canned, setCanned] = useState<Canned[]>([]);
  const [usedCanned, setUsedCanned] = useState<string | null>(null);
  const [pick, setPick] = useState(0);
  const [handing, setHanding] = useState(false);
  const [closing, setClosing] = useState(false);
  const [leading, setLeading] = useState(false);
  const list = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const load = useCallback(() => action<Detail>('livechat.get', { id }).then(setD, fail), [id, fail]);
  useEffect(() => {
    void load();
    action<{ items: Canned[] }>('livechat.canned').then((r) => setCanned(r.items), fail);
  }, [load, fail]);
  useEffect(() => {
    if (pulse) void load();
  }, [pulse, load]);
  useEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight });
  }, [d?.messages.length]);

  const slash = body.startsWith('/') && !body.includes(' ') && !body.includes('\n');
  const matches = useMemo(() => (slash ? canned.filter((c) => c.shortcut.startsWith(body.slice(1).toLowerCase())).slice(0, 6) : []), [slash, body, canned]);
  useEffect(() => setPick(0), [body]);

  if (!d)
    return (
      <div className="lc-thread">
        <Skeleton h={56} r="var(--radius-md)" />
        <Skeleton h={300} r="var(--radius-md)" />
      </div>
    );
  const mine = d.operator === me;
  const free = !d.operator;
  const blocked = !!d.operator && !mine && !d.can.manage;
  const closed = d.state === 'closed';
  const useCanned = (c: Canned) => {
    setBody(c.body);
    setUsedCanned(c.id);
    input.current?.focus();
  };
  const send = async () => {
    const text = body.trim();
    if (!text || blocked) return;
    const ok = await run(() => action('livechat.send', { id: d.id, body: text, canned: usedCanned }));
    if (ok) {
      setBody('');
      setUsedCanned(null);
      await load();
      onChanged();
      input.current?.focus();
    }
  };
  const act = async (name: string, input: Record<string, unknown>, done?: string) => {
    if (await run(() => action(name, { id: d.id, ...input }), done)) {
      await load();
      onChanged();
      return true;
    }
    return false;
  };
  const makeLead = async () => {
    setLeading(true);
    try {
      const lead = await action<{ id: string }>('crm.save_lead', {
        title: t('lc.lead_title', { name: d.name }),
        contact_name: d.name,
        email: d.email,
        source: 'web',
        owner: me,
        note: d.messages
          .filter((m) => m.author !== 'system')
          .map((m) => `${m.author === 'visitor' ? d.name : m.author === 'bot' ? d.bot_name : (m.user_name ?? '')}: ${botLine(m.author, m.body)?.text ?? m.body}`)
          .join('\n')
          .slice(0, 2000),
      });
      await action('livechat.link_lead', { id: d.id, lead: lead.id });
      toast(t('lc.lead_made'));
      await load();
    } catch (e) {
      fail(e);
    } finally {
      setLeading(false);
    }
  };
  const lastStaff = [...d.messages].reverse().find((m) => m.author === 'staff');
  const lastMsg = d.messages[d.messages.length - 1];

  return (
    <div className="lc-thread">
      <header className="lc-thread-head">
        <IconButton icon="back" label={t('lc.back')} className="lc-back" onClick={onBack} />
        <span className="lc-avatar lg" aria-hidden="true">
          {initials(d.name)}
        </span>
        <div className="lc-thread-who">
          <h2>{d.name}</h2>
          <p className="muted">
            {d.ref}
            {d.email ? (
              <>
                {' · '}
                <a href={`mailto:${d.email}`}>{d.email}</a>
              </>
            ) : null}
            {d.page ? ` · ${t('lc.from_page', { page: d.page })}` : null}
          </p>
        </div>
        <div className="lc-thread-actions">
          {closed ? (
            d.bot_solved ? (
              <StatusBadge tone="ok" icon="sparkle" label={t('lc.bot_solved')} />
            ) : (
              <StatusBadge tone="neutral" icon="lock" label={t('lc.closed')} />
            )
          ) : d.bot ? (
            <>
              <StatusBadge tone="info" icon="sparkle" label={t('lc.with_bot')} />
              <Button size="sm" variant="primary" icon="check" busy={busy} onClick={() => void act('livechat.take', {}, t('lc.taken'))}>
                {t('lc.take')}
              </Button>
            </>
          ) : free ? (
            <Button size="sm" variant="primary" icon="check" busy={busy} onClick={() => void act('livechat.take', {}, t('lc.taken'))}>
              {t('lc.take')}
            </Button>
          ) : mine ? (
            <StatusBadge tone="ok" icon="chat" label={t('lc.yours')} />
          ) : (
            <StatusBadge tone="info" icon="users" label={t('lc.with', { name: d.operator_name ?? '' })} />
          )}
          {!closed && (mine || free || d.can.manage) ? (
            <Button size="sm" icon="transfer" onClick={() => setHanding(true)}>
              {t('lc.hand_over')}
            </Button>
          ) : null}
          {!closed && (mine || d.can.manage) ? (
            <Button size="sm" icon="lock" onClick={() => setClosing(true)}>
              {t('lc.close')}
            </Button>
          ) : null}
        </div>
      </header>

      <div className="lc-thread-body">
        <div className="lc-msgs" ref={list} aria-live="polite" aria-relevant="additions">
          <p className="lc-day">{t('lc.started', { when: dateTime(d.started_at) })}</p>
          {d.messages.map((m) => {
            const said = botLine(m.author, m.body);
            const as = said?.as ?? m.author;
            return as === 'system' ? (
              <p key={m.id} className="lc-system">
                {systemText(m.body)}
              </p>
            ) : (
              <div key={m.id} className={`lc-msg ${as}`}>
                <span className="lc-msg-who">
                  {as === 'bot' ? (
                    <>
                      <Icon name="sparkle" size={12} /> {d.bot_name}
                    </>
                  ) : as === 'visitor' ? (
                    d.name
                  ) : (
                    m.user_name
                  )}
                </span>
                <p>{said?.text ?? m.body}</p>
                <time dateTime={m.at}>{clock(m.at)}</time>
              </div>
            );
          })}
          {lastStaff && lastMsg?.id === lastStaff.id && d.seen_by_visitor ? <p className="lc-seen">{t('lc.seen')}</p> : null}
          {closed && d.rating ? (
            <div className="lc-rating-note">
              <span aria-hidden="true">{FACE[d.rating]}</span>
              <span>
                <strong>{t(`lc.rating_${d.rating}` as MessageKey)}</strong>
                {d.rating_note ? <q>{d.rating_note}</q> : null}
              </span>
            </div>
          ) : null}
        </div>
        <aside className="lc-side" aria-label={t('lc.about')}>
          <dl className="lc-facts">
            <dt>{t('lc.first_reply')}</dt>
            <dd>
              {d.first_reply_seconds === null && d.messages.some((m) => m.author === 'bot')
                ? t('lc.bot_answered')
                : d.offline && d.first_reply_seconds === null
                  ? t('lc.left_message')
                  : seconds(d.first_reply_seconds)}
            </dd>
            {d.closed_at ? (
              <>
                <dt>{t('lc.closed_at')}</dt>
                <dd>{dateTime(d.closed_at)}</dd>
              </>
            ) : null}
          </dl>
          {d.lead ? (
            <Link className="lc-lead" to={`/crm?lead=${d.lead.id}`}>
              <Icon name="star" size={16} />
              <span>
                <small>{t('lc.opportunity')}</small>
                <strong>{d.lead.title}</strong>
              </span>
            </Link>
          ) : d.can.lead && can('crm.use') ? (
            <Button size="sm" icon="star" busy={leading} onClick={() => void makeLead()}>
              {t('lc.make_lead')}
            </Button>
          ) : null}
          {d.earlier.length ? (
            <div className="lc-earlier">
              <h3>{t('lc.earlier')}</h3>
              <ul>
                {d.earlier.map((e) => (
                  <li key={e.id}>
                    <button type="button" className="link" onClick={() => onOpen(e.id)}>
                      {e.ref}
                    </button>
                    <span className="muted"> · {since(e.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </aside>
      </div>

      {blocked ? (
        <p className="lc-note">
          <Icon name="lock" size={14} /> {t('lc.blocked', { name: d.operator_name ?? '' })}
        </p>
      ) : (
        <form
          className="lc-composer"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          {matches.length ? (
            <ul className="lc-suggest" role="listbox" aria-label={t('lc.canned_title')}>
              {matches.map((c, i) => (
                <li key={c.id} role="option" aria-selected={i === pick}>
                  <button type="button" className={i === pick ? 'active' : ''} onMouseDown={(e) => e.preventDefault()} onClick={() => useCanned(c)}>
                    <strong>/{c.shortcut}</strong>
                    <span>{c.body}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <textarea
            ref={input}
            className="input textarea"
            rows={2}
            maxLength={2000}
            value={body}
            placeholder={closed ? t('lc.type_reopen') : t('lc.type')}
            aria-label={t('lc.type')}
            onChange={(e) => {
              setBody(e.target.value);
              if (!e.target.value) setUsedCanned(null);
            }}
            onKeyDown={(e) => {
              if (matches.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                e.preventDefault();
                setPick((p) => (p + (e.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length);
              } else if (matches.length && (e.key === 'Enter' || e.key === 'Tab')) {
                e.preventDefault();
                useCanned(matches[pick]!);
              } else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <div className="lc-composer-foot">
            <span className="muted lc-hint">{t('lc.hint')}</span>
            <Button type="submit" variant="primary" icon="send" busy={busy} disabled={!body.trim()}>
              {free && !closed ? t('lc.take_send') : t('lc.send')}
            </Button>
          </div>
        </form>
      )}

      {handing ? (
        <Dialog title={t('lc.hand_title')} onClose={() => setHanding(false)} size="sm">
          <ul className="lc-team">
            {team
              .filter((p) => p.id !== d.operator)
              .map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="lc-team-row"
                    onClick={async () => {
                      if (await act('livechat.transfer', { to: p.id }, t('lc.handed', { name: p.name }))) setHanding(false);
                    }}
                  >
                    <span className="lc-avatar" aria-hidden="true">
                      {initials(p.name)}
                    </span>
                    <span>{p.id === me ? t('lc.me', { name: p.name }) : p.name}</span>
                    <StatusBadge tone={p.online ? 'ok' : 'neutral'} icon={p.online ? 'circle' : 'moon'} label={p.online ? t('lc.online') : t('lc.offline')} />
                  </button>
                </li>
              ))}
          </ul>
          <p className="muted small">{t('lc.hand_hint')}</p>
        </Dialog>
      ) : null}
      {closing ? (
        <Confirm
          title={t('lc.close_q')}
          body={t('lc.close_body')}
          confirm={t('lc.close')}
          busy={busy}
          onClose={() => setClosing(false)}
          onConfirm={async () => {
            if (await act('livechat.close', {}, t('lc.closed_ok'))) setClosing(false);
          }}
        />
      ) : null}
    </div>
  );
}

function CannedTab() {
  const { data, reload } = useAction<{ items: Canned[]; can: { manage: boolean } }>('livechat.canned');
  const [edit, setEdit] = useState<Canned | 'new' | null>(null);
  const [removing, setRemoving] = useState<Canned | null>(null);
  const { run, busy } = useSave();
  if (!data) return <Skeleton h={300} r="var(--radius-lg)" />;
  return (
    <section className="panel lc-canned">
      <div className="panel-head">
        <div>
          <h2>{t('lc.canned_title')}</h2>
          <p className="muted">{t('lc.canned_sub')}</p>
        </div>
        {data.can.manage ? (
          <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
            {t('lc.canned_new')}
          </Button>
        ) : null}
      </div>
      {!data.items.length ? (
        <EmptyState icon="note" title={t('lc.canned_empty')} body={t('lc.canned_empty_body')} />
      ) : (
        <ul className="lc-canned-list">
          {data.items.map((c) => (
            <li key={c.id}>
              <code>/{c.shortcut}</code>
              <p>{c.body}</p>
              <span className="muted small">{tn('lc.used', c.uses)}</span>
              {data.can.manage ? (
                <span className="lc-canned-acts">
                  <IconButton icon="edit" label={t('lc.edit')} onClick={() => setEdit(c)} />
                  <IconButton icon="trash" label={t('lc.remove')} onClick={() => setRemoving(c)} />
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {edit ? <CannedForm item={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={() => (setEdit(null), void reload())} /> : null}
      {removing ? (
        <Confirm
          danger
          title={t('lc.canned_remove_q', { s: removing.shortcut })}
          body={t('lc.canned_remove_body')}
          confirm={t('lc.remove')}
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            if (await run(() => action('livechat.remove_canned', { id: removing.id }), t('lc.removed'))) {
              setRemoving(null);
              void reload();
            }
          }}
        />
      ) : null}
    </section>
  );
}

function CannedForm({ item, onClose, onSaved }: { item: Canned | null; onClose: () => void; onSaved: () => void }) {
  const [shortcut, setShortcut] = useState(item?.shortcut ?? '');
  const [body, setBody] = useState(item?.body ?? '');
  const [tried, setTried] = useState(false);
  const { run, busy } = useSave();
  const sc = shortcut.trim().replace(/^\//, '').toLowerCase();
  const errors = {
    shortcut: !sc ? t('lc.need_shortcut') : !/^[a-z0-9_-]{1,30}$/.test(sc) ? t('lc.bad_shortcut') : null,
    body: !body.trim() ? t('lc.need_body') : null,
  };
  const save = async () => {
    setTried(true);
    if (errors.shortcut || errors.body) return;
    if (await run(() => action('livechat.save_canned', { id: item?.id, shortcut: sc, body: body.trim() }), t('lc.saved'))) onSaved();
  };
  return (
    <Dialog
      title={item ? t('lc.canned_edit') : t('lc.canned_new')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('lc.cancel')}
          </Button>
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            {t('lc.save_btn')}
          </Button>
        </>
      }
    >
      <Field label={t('lc.shortcut')} hint={t('lc.shortcut_hint')} required error={tried ? errors.shortcut : null}>
        <input className="input" value={shortcut} maxLength={31} onChange={(e) => setShortcut(e.target.value)} autoFocus />
      </Field>
      <Field label={t('lc.reply')} required error={tried ? errors.body : null} counter={`${body.length}/2000`}>
        <textarea className="input textarea" rows={5} maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
    </Dialog>
  );
}

function ReportTab() {
  const [days, setDays] = useState(30);
  const { data } = useAction<Report>('livechat.report', { days });
  if (!data) return <Skeleton h={360} r="var(--radius-lg)" />;
  const rated = data.ratings.good + data.ratings.okay + data.ratings.bad;
  const max = Math.max(1, ...data.by_hour);
  const pct = (n: number) => (rated ? Math.round((n / rated) * 100) : 0);
  return (
    <div className="lc-report">
      <div className="lc-report-tools">
        <div className="segmented" role="group" aria-label={t('lc.period')}>
          {[7, 30, 90].map((n) => (
            <button key={n} type="button" aria-pressed={days === n} className={days === n ? 'active' : ''} onClick={() => setDays(n)}>
              {t('lc.days', { n })}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          icon="download"
          onClick={() =>
            downloadCsv(
              `live-chat-${data.days}-days.csv`,
              [t('lc.r_operator'), t('lc.r_chats'), t('lc.r_sent'), t('lc.r_reply'), t('lc.r_good')],
              data.operators.map((o) => [o.name, o.chats, o.sent, o.median_reply ?? '', o.good_pct ?? '']),
            )
          }
        >
          {t('lc.export')}
        </Button>
      </div>
      <div className="rep-kpis">
        <Kpi label={t('lc.k_chats')} value={String(data.chats)} hint={tn('lc.k_offline', data.offline)} />
        <Kpi label={t('lc.k_reply')} value={seconds(data.median_reply)} hint={t('lc.k_reply_hint')} />
        <Kpi label={t('lc.k_good')} value={rated ? `${pct(data.ratings.good)} %` : '—'} hint={tn('lc.k_rated', rated)} />
        <Kpi label={t('lc.k_unanswered')} value={String(data.unanswered)} warn={data.unanswered > 0} />
        <Kpi label={t('lc.k_leads')} value={String(data.leads)} />
        {data.bot_solved ? <Kpi label={t('lc.k_bot')} value={String(data.bot_solved)} /> : null}
      </div>
      <div className="lc-report-grid">
        <section className="panel">
          <h2>{t('lc.when')}</h2>
          <p className="muted small">{t('lc.when_sub')}</p>
          <div className="lc-hours" role="img" aria-label={t('lc.when_aria', { h: data.by_hour.indexOf(Math.max(...data.by_hour)) })}>
            {data.by_hour.map((n, h) => (
              <span key={h} className="lc-hour" title={`${String(h).padStart(2, '0')}:00 · ${n}`}>
                <span className="lc-hour-bar" style={{ height: `${Math.round((n / max) * 100)}%` }} />
                {h % 3 === 0 ? <small>{String(h).padStart(2, '0')}</small> : <small aria-hidden="true">&nbsp;</small>}
              </span>
            ))}
          </div>
        </section>
        <section className="panel">
          <h2>{t('lc.how')}</h2>
          {rated ? (
            <ul className="lc-ratings">
              {([3, 2, 1] as const).map((r) => {
                const n = r === 3 ? data.ratings.good : r === 2 ? data.ratings.okay : data.ratings.bad;
                return (
                  <li key={r}>
                    <span aria-hidden="true">{FACE[r]}</span>
                    <span>{t(`lc.rating_${r}` as MessageKey)}</span>
                    <span className="lc-rating-bar">
                      <span className={`r${r}`} style={{ width: `${pct(n)}%` }} />
                    </span>
                    <strong>{n}</strong>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="muted">{t('lc.no_ratings')}</p>
          )}
        </section>
      </div>
      <section className="panel">
        <h2>{t('lc.team')}</h2>
        {data.operators.length ? (
          <TableWrap>
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('lc.r_operator')}</th>
                  <th className="num">{t('lc.r_chats')}</th>
                  <th className="num">{t('lc.r_sent')}</th>
                  <th className="num">{t('lc.r_reply')}</th>
                  <th className="num">{t('lc.r_good')}</th>
                </tr>
              </thead>
              <tbody>
                {data.operators.map((o) => (
                  <tr key={o.id}>
                    <td>{o.name}</td>
                    <td className="num">{o.chats}</td>
                    <td className="num">{o.sent}</td>
                    <td className="num">{seconds(o.median_reply)}</td>
                    <td className="num">{o.good_pct === null ? '—' : `${o.good_pct} %`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        ) : (
          <p className="muted">{t('lc.no_chats')}</p>
        )}
      </section>
      {data.comments.length ? (
        <section className="panel">
          <h2>{t('lc.comments')}</h2>
          <ul className="lc-comments">
            {data.comments.map((c) => (
              <li key={c.id}>
                <span aria-label={t(`lc.rating_${c.rating}` as MessageKey)}>{FACE[c.rating]}</span>
                <div>
                  <q>{c.note}</q>
                  <p className="muted small">
                    {c.name} · {c.ref}
                    {c.at ? ` · ${since(c.at)}` : ''}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

interface BotTopic {
  id?: string;
  key: string;
  label: string;
  keywords: string | null;
  answer: string;
  after: 'ask' | 'person' | 'contact';
  active: boolean;
  uses: number;
}
interface Bot {
  enabled: boolean;
  name: string;
  intro: string | null;
  topics: (Omit<BotTopic, 'key'> & { id: string })[];
  last30: { solved: number; helped: number; chats: number };
  can: { manage: boolean };
}
const AFTER: BotTopic['after'][] = ['ask', 'person', 'contact'];
let topicKey = 0;
const newKey = () => `t${++topicKey}`;

/** The chat assistant: switch it on, give it topics, and try what it would say. */
function BotTab() {
  const { data, reload } = useAction<Bot>('livechat.bot');
  const [enabled, setEnabled] = useState(false);
  const [name, setName] = useState('');
  const [intro, setIntro] = useState('');
  const [list, setList] = useState<BotTopic[]>([]);
  const [tried, setTried] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<{ topic: { label: string; answer: string } | null } | null>(null);
  const { run, busy } = useSave();
  const { fail } = useUi();
  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setName(data.name);
    setIntro(data.intro ?? '');
    setList(data.topics.map((x) => ({ ...x, key: x.id })));
  }, [data]);
  if (!data) return <Skeleton h={420} r="var(--radius-lg)" />;
  const manage = data.can.manage;
  const patch = (key: string, p: Partial<BotTopic>) => setList((l) => l.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const move = (i: number, by: number) =>
    setList((l) => {
      const n = [...l];
      [n[i], n[i + by]] = [n[i + by]!, n[i]!];
      return n;
    });
  const add = () => {
    const key = newKey();
    setList((l) => [...l, { key, label: '', keywords: '', answer: '', after: 'ask', active: true, uses: 0 }]);
    window.setTimeout(() => document.getElementById(`bt-${key}`)?.focus(), 30);
  };
  const bad = (x: BotTopic) => !x.label.trim() || !x.answer.trim();
  const save = async () => {
    setTried(true);
    if (list.some(bad)) return;
    const ok = await run(
      () =>
        action('livechat.save_bot', {
          enabled,
          name: name.trim() || 'Assistant',
          intro: intro.trim() || null,
          topics: list.map((x) => ({ id: x.id ?? null, label: x.label.trim(), keywords: x.keywords?.trim() || null, answer: x.answer.trim(), after: x.after, active: x.active })),
        }),
      t('lc.bot_saved'),
    );
    if (ok) {
      setTried(false);
      void reload();
    }
  };
  const test = async () => {
    if (!question.trim()) return;
    await action<{ topic: { label: string; answer: string } | null }>('livechat.try_bot', { text: question.trim() }).then(setAnswer, fail);
  };
  const rate = data.last30.helped ? Math.round((data.last30.solved / data.last30.helped) * 100) : null;
  return (
    <div className="lc-bot">
      <div className="rep-kpis">
        <Kpi label={t('lc.bot_k_helped')} value={String(data.last30.helped)} hint={t('lc.days', { n: 30 })} />
        <Kpi label={t('lc.bot_k_solved')} value={String(data.last30.solved)} />
        <Kpi label={t('lc.bot_k_rate')} value={rate === null ? '—' : `${rate} %`} />
      </div>
      <div className="lc-bot-grid">
        <section className="panel lc-bot-main">
          <div className="panel-head">
            <div>
              <h2>{t('lc.bot_title')}</h2>
              <p className="muted">{t('lc.bot_sub')}</p>
            </div>
          </div>
          {!manage ? (
            <p className="callout callout-info">
              <Icon name="lock" size={16} /> {t('lc.bot_readonly')}
            </p>
          ) : null}
          <fieldset className="lc-bot-fields" disabled={!manage}>
            <div className="lc-setting-row">
              <div>
                <strong>{t('lc.bot_on')}</strong>
                <p className="muted small">{t('lc.bot_on_hint')}</p>
              </div>
              <Switch checked={enabled} onChange={setEnabled} label={t('lc.bot_on')} />
            </div>
            <div className="lc-bot-pair">
              <Field label={t('lc.bot_name')} hint={t('lc.bot_name_hint')}>
                <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label={t('lc.bot_intro')} hint={t('lc.bot_intro_hint')}>
                <input className="input" value={intro} maxLength={500} placeholder={t('cb.intro')} onChange={(e) => setIntro(e.target.value)} />
              </Field>
            </div>
            <div className="lc-bot-topics-head">
              <div>
                <h3>{t('lc.bot_topics')}</h3>
                <p className="muted small">{t('lc.bot_topics_sub')}</p>
              </div>
              <Button size="sm" icon="plus" onClick={add} disabled={list.length >= 30}>
                {t('lc.bot_add')}
              </Button>
            </div>
            {!list.length ? (
              <EmptyState icon="sparkle" title={t('lc.bot_empty')} body={t('lc.bot_empty_body')} />
            ) : (
              <ol className="lc-bot-topics">
                {list.map((x, i) => (
                  <li key={x.key} className={`lc-bot-topic${x.active ? '' : ' off'}`}>
                    <div className="lc-bot-topic-head">
                      <span className="lc-bot-n" aria-hidden="true">
                        {i + 1}
                      </span>
                      <Field label={t('lc.bot_label')} required error={tried && !x.label.trim() ? t('lc.bot_need_label') : null}>
                        <input id={`bt-${x.key}`} className="input" value={x.label} maxLength={60} onChange={(e) => patch(x.key, { label: e.target.value })} />
                      </Field>
                      <div className="lc-bot-topic-tools">
                        {x.id ? <span className="muted small">{tn('lc.bot_used', x.uses)}</span> : null}
                        <Switch checked={x.active} onChange={(v) => patch(x.key, { active: v })} label={t('lc.bot_active')} />
                        <IconButton icon="up" label={t('lc.bot_up')} disabled={i === 0} onClick={() => move(i, -1)} />
                        <IconButton icon="down" label={t('lc.bot_down')} disabled={i === list.length - 1} onClick={() => move(i, 1)} />
                        <IconButton icon="trash" label={t('lc.bot_remove')} onClick={() => setList((l) => l.filter((y) => y.key !== x.key))} />
                      </div>
                    </div>
                    <Field label={t('lc.bot_keywords')} hint={t('lc.bot_keywords_hint')}>
                      <input className="input" value={x.keywords ?? ''} maxLength={500} onChange={(e) => patch(x.key, { keywords: e.target.value })} />
                    </Field>
                    <Field label={t('lc.bot_answer')} required error={tried && !x.answer.trim() ? t('lc.bot_need_answer') : null} counter={`${x.answer.length}/2000`}>
                      <textarea className="input textarea" rows={3} maxLength={2000} value={x.answer} onChange={(e) => patch(x.key, { answer: e.target.value })} />
                    </Field>
                    <div className="lc-bot-after" role="radiogroup" aria-label={t('lc.bot_after')}>
                      <span className="small muted">{t('lc.bot_after')}</span>
                      {AFTER.map((a) => (
                        <button key={a} type="button" role="radio" aria-checked={x.after === a} className={`chip${x.after === a ? ' active' : ''}`} onClick={() => patch(x.key, { after: a })}>
                          {t(`lc.bot_after_${a}` as MessageKey)}
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </fieldset>
          {manage ? (
            <div className="lc-bot-save">
              <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
                {t('lc.save_btn')}
              </Button>
            </div>
          ) : null}
        </section>
        <aside className="panel lc-bot-try" aria-labelledby="lc-bot-try">
          <h2 id="lc-bot-try">{t('lc.bot_try')}</h2>
          <p className="muted small">{t('lc.bot_try_hint')}</p>
          <form
            className="lc-bot-try-form"
            onSubmit={(e) => {
              e.preventDefault();
              void test();
            }}
          >
            <input className="input" value={question} maxLength={500} onChange={(e) => (setQuestion(e.target.value), setAnswer(null))} aria-label={t('lc.bot_try')} />
            <Button type="submit" icon="send" disabled={!question.trim()}>
              {t('lc.bot_try_go')}
            </Button>
          </form>
          {answer ? (
            answer.topic ? (
              <div className="lc-bot-preview" role="status">
                <span className="lc-msg-who">
                  <Icon name="sparkle" size={12} /> {t('lc.bot_try_hit', { label: answer.topic.label })}
                </span>
                <p>{answer.topic.answer}</p>
              </div>
            ) : (
              <p className="callout callout-warn" role="status">
                <Icon name="alert" size={16} /> {t('lc.bot_try_none')}
              </p>
            )
          ) : null}
        </aside>
      </div>
    </div>
  );
}
function SettingsDialog({ tenant, onClose, onSaved }: { tenant: string; onClose: () => void; onSaved: () => void }) {
  const { data } = useAction<Settings>('livechat.settings');
  const [s, setS] = useState<Settings | null>(null);
  const { run, busy } = useSave();
  const { toast } = useUi();
  useEffect(() => {
    if (data) setS(data);
  }, [data]);
  const save = async () => {
    if (!s) return;
    if (
      await run(
        () => action('livechat.save_settings', { ...s, title: s.title.trim() || t('lc.default_title'), welcome: s.welcome?.trim() || null, offline_text: s.offline_text?.trim() || null }),
        t('lc.saved'),
      )
    ) {
      onSaved();
      onClose();
    }
  };
  const url = chatUrl(tenant);
  return (
    <Dialog
      title={t('lc.settings')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('lc.cancel')}
          </Button>
          <Button variant="primary" busy={busy} disabled={!s} onClick={() => void save()}>
            {t('lc.save_btn')}
          </Button>
        </>
      }
    >
      {!s ? (
        <Skeleton h={300} />
      ) : (
        <div className="lc-settings">
          <div className="lc-setting-row">
            <div>
              <strong>{t('lc.s_enabled')}</strong>
              <p className="muted small">{t('lc.s_enabled_hint')}</p>
            </div>
            <Switch checked={s.enabled} onChange={(v) => setS({ ...s, enabled: v })} label={t('lc.s_enabled')} />
          </div>
          <Field label={t('lc.s_title')}>
            <input className="input" value={s.title} maxLength={80} onChange={(e) => setS({ ...s, title: e.target.value })} />
          </Field>
          <Field label={t('lc.s_welcome')} hint={t('lc.s_welcome_hint')}>
            <textarea className="input textarea" rows={2} maxLength={500} value={s.welcome ?? ''} onChange={(e) => setS({ ...s, welcome: e.target.value })} />
          </Field>
          <Field label={t('lc.s_offline')} hint={t('lc.s_offline_hint')}>
            <textarea className="input textarea" rows={2} maxLength={500} value={s.offline_text ?? ''} onChange={(e) => setS({ ...s, offline_text: e.target.value })} />
          </Field>
          <Field label={t('lc.s_email')}>
            <select className="input" value={s.ask_email} onChange={(e) => setS({ ...s, ask_email: e.target.value as Settings['ask_email'] })}>
              <option value="no">{t('lc.s_email_no')}</option>
              <option value="optional">{t('lc.s_email_optional')}</option>
              <option value="required">{t('lc.s_email_required')}</option>
            </select>
          </Field>
          <div className="lc-setting-row">
            <div>
              <strong>{t('lc.s_rating')}</strong>
              <p className="muted small">{t('lc.s_rating_hint')}</p>
            </div>
            <Switch checked={s.rating} onChange={(v) => setS({ ...s, rating: v })} label={t('lc.s_rating')} />
          </div>
          <div className="lc-link-box">
            <p className="small">
              <strong>{t('lc.s_where')}</strong>
            </p>
            <p className="muted small">{t('lc.s_where_hint')}</p>
            <div className="lc-link">
              <code>{url}</code>
              <IconButton
                icon="copy"
                label={t('lc.copy')}
                onClick={() =>
                  void navigator.clipboard.writeText(url).then(
                    () => toast(t('lc.copied')),
                    () => undefined,
                  )
                }
              />
              <a className="icon-btn btn-ghost" href={url} target="_blank" rel="noreferrer" aria-label={t('lc.open_page')} title={t('lc.open_page')}>
                <Icon name="external" />
              </a>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
